import { createContext } from "react";
import type { Editor } from "@tiptap/react";
import { MentionComponentAttributeNames } from "shared/components/Editor/core/extentions/mentions/types";
import type { SmsVariable } from "views/settings-communication/api/types";
import type { ContactUnitLinks } from "../api/crm";

// The two link variable keys are BE-driven (variables carry requires_unit),
// but we map a key -> which side of the unit_links payload to read.
export type LinkType = "lead.application_link" | "lead.request_showing_link";

const LINK_KEYS = new Set<string>([
  "lead.application_link",
  "lead.request_showing_link"
]);

export const isLinkVariableKey = (key: string): boolean => LINK_KEYS.has(key);

// Custom attrs added on top of the base mention node so a chip can remember
// which link variable it is (linkType) and which unit was chosen (unitId).
// Name variables leave both null and behave exactly as before.
export const ATTR_LINK_TYPE = "linkType";
export const ATTR_UNIT_ID = "unitId";

export const MENTION_ENTITY_NAME = "partner_user";

interface InboxVariableData {
  variables: SmsVariable[];
  units: ContactUnitLinks[];
}

// Variable/unit data for the chip node views. Chips must NOT read this from
// the mention extension's options: the tiptap editor instantiates extensions
// once on mount (before the unit_links/variables queries resolve) and never
// re-registers them, so options stay frozen with empty arrays. Node views
// render in portals under EditorContent, so a provider wrapping the editor
// reaches the chips and re-renders them whenever the data lands.
export const InboxVariableDataContext = createContext<InboxVariableData>({
  variables: [],
  units: []
});

export const linkForType = (
  unit: ContactUnitLinks,
  linkType: LinkType
): { short_url: string; display_url: string } | null =>
  linkType === "lead.application_link"
    ? unit.application_link
    : unit.request_showing_link;

const NO_LINK_HINT = "No link available for this unit";

const REALTOR_LINK_HINTS: Record<LinkType, string> = {
  "lead.request_showing_link":
    "Realtors book through Invite to schedule on the lead page",
  "lead.application_link": "Realtors can't apply for a unit"
};

// Why a unit can't be picked for a link variable. A realtor's unit gets
// neither link (see ContactUnitLinks.realtor_lead), so say why instead of the
// generic copy.
export const unavailableLinkHint = (
  unit: ContactUnitLinks,
  linkType: LinkType | null
): string =>
  linkType && unit.realtor_lead ? REALTOR_LINK_HINTS[linkType] : NO_LINK_HINT;

// Fallback-required, unit-scoped VALUE variables (not link variables — they
// carry no unit picker of their own, but their resolved value depends on
// which unit the message is about). Keyed by variable key -> how to read
// that unit's value off ContactUnitLinks.
const UNIT_SCOPED_VALUE_KEYS: Record<
  string,
  (unit: ContactUnitLinks) => string | null
> = {
  "lead.property_name": (unit) => unit.property_name,
  "lead.property_address": (unit) => unit.property_address,
  "lead.listing_link": (unit) => unit.listing_link
};

// A contact with multiple leads has no reliable signal for which one a
// message is "about" (see LeadScopedVariableKeys server-side), so a unit-
// scoped value chip seeds with the author's fallback text. Picking a Unit
// for a link chip is what actually disambiguates the lead, so sync any
// unit-scoped value chip(s) in the doc to THAT unit's real value right when
// it's chosen — manually (InboxVariableChip's unit picker) or automatically
// (resolveUnresolvedLinkChips in link-variable-utils.ts). Lives here (a leaf
// module) rather than link-variable-utils.ts to avoid a circular import with
// InboxVariableChip.tsx, which needs to call this too.
export const syncUnitScopedValueChips = (
  editor: Editor,
  unit: ContactUnitLinks
): void => {
  const updates: { pos: number; attrs: Record<string, unknown> }[] = [];
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name !== "mention") return;
    const attrs = node.attrs as Record<string, unknown>;
    const key = attrs[MentionComponentAttributeNames.ID] as string;
    const valueOf = UNIT_SCOPED_VALUE_KEYS[key];
    const value = valueOf?.(unit);
    if (!value) return;
    updates.push({
      pos,
      attrs: { ...attrs, [MentionComponentAttributeNames.Title]: value }
    });
  });
  if (updates.length === 0) return;
  const tr = editor.state.tr;
  updates.forEach(({ pos, attrs }) => tr.setNodeMarkup(pos, undefined, attrs));
  editor.view.dispatch(tr);
};
